import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

/**
 * Database.ts is a utility class for interacting with pouch db from the smartchart 
 * questionnaire engine.  
 * 
 * Last Edited:  5/25/22 Travis
 * 
 * UseFull Things:
 * calculate the size of the current local database from the console.  Useful to make sure 
 * that we are not blowing up the localstorage as we save, as this can lead to issues on 
 * mobile. Running this line of code in the dev console will output the size of each item 
 * in the local storage.
 * var _lsTotal=0,_xLen,_x;for(_x in localStorage){ if(!localStorage.hasOwnProperty(_x)){continue;} _xLen= ((localStorage[_x].length + _x.length)* 2);_lsTotal+=_xLen; console.log(_x.substr(0,50)+" = "+ (_xLen/1024).toFixed(2)+" KB")};console.log("Total = " + (_lsTotal / 1024).toFixed(2) + " KB");

 */

/* eslint-disable no-unused-expressions */
import PouchDB from 'pouchdb';
// type DatabaseTools
const watchers = {};
export class DatabaseTools {
  constructor(agency, workerNumber, keycloak, patientNumber) {
    _defineProperty(this, "isError", false);

    _defineProperty(this, "errorMessage", '');

    _defineProperty(this, "localDB", void 0);

    _defineProperty(this, "inProg", void 0);

    _defineProperty(this, "saveState", void 0);

    _defineProperty(this, "dbName", void 0);

    _defineProperty(this, "keycloak", void 0);

    _defineProperty(this, "saveToPouch", async (documentId, documentObj) => {
      try {
        await this.save(documentId, documentObj);
      } catch (err) {
        console.log('there was an error saving to pouch:', err);
      }
    });

    _defineProperty(this, "save", async (documentId, documentObj) => {
      const db = new PouchDB(this.dbName.toLowerCase());

      try {
        await db.upsert(documentId, doc => documentObj);
        this.callWatchers(documentId, documentObj);
      } catch (err) {
        console.log('An error occured during the Pouch Save process:', err);
      }
    });

    _defineProperty(this, "delete", async documentId => {
      this.localDB.get(documentId).then(doc => {
        // eslint-disable-next-line no-underscore-dangle
        this.localDB.remove(documentId, doc._rev).then(() => {
          // pass the patient object to the documentId watcher callback.
          this.callWatchers(documentId, doc);
        });
      }).catch(e => {
        this.isError = true;
        this.errorMessage = e;
        console.log(e);
      });
    });

    _defineProperty(this, "retrieve", async documentId => this.localDB.get(documentId).catch(e => {
      /*
       * There is an issue in pouch during initial replication that it will throw false
       * 404 errors as part of the replication proess.  If we see these on retreval, ignore
       * them. Pouch has to requery Couch during the replication process.
       */
      if (e.error && e.name === 'not_found' && e.reason === 'missing') {
        return;
      }

      this.isError = true;
      this.errorMessage = e;
      console.log(e);
    }));

    _defineProperty(this, "destroyWatchers", documentId => {
      watchers[documentId] = [];
    });

    _defineProperty(this, "watchItem", async (documentId, cb) => {
      if (watchers[documentId]) {
        watchers[documentId].push(cb);
      } else {
        watchers[documentId] = [cb];
      }
    });

    _defineProperty(this, "callWatchers", (documentId, obj) => {
      if (watchers[documentId]) {
        watchers[documentId].forEach(cb => {
          cb(obj);
        });
      }
    });

    _defineProperty(this, "getErrorStatus", () => this.isError);

    _defineProperty(this, "getErrorMessage", () => this.errorMessage);

    this.inProg = false; // this is the database name in both couch and pouch

    this.dbName = `dashboard_${agency}_${workerNumber}`;
    this.keycloak = keycloak;
    this.localDB = new PouchDB(this.dbName.toLowerCase(), {
      revs_limit: 1,
      auto_compaction: true
    });
    this.localDB.info().then(() => console.log('DB:::local db initialized. ')).catch(e => {
      console.log('There was an error initializing the local database. ', e);
      this.isError = true;
      this.errorMessage = e;
    });
    /**
     * if there is a patient number passed to the constructor, and now that the databases
     * have synced. check if there is a document for the current patient.  If there is not
     * then putIfNotExists will create a bare bones document.
     */

    if (patientNumber) {
      this.localDB.putIfNotExists({
        _id: patientNumber
      }).catch(e => {
        /*
         * There is an issue in pouch during initial replication that it will throw false
         * 404 errors as part of the replication process.  If we see these on retreval, ignore
         * them. Pouch has to requery Couch during the replication process.
         */
        if (e.error && e.name === 'not_found' && e.reason === 'missing') {
          return;
        }

        console.log(`There was an error creating a database object for patient ${patientNumber}. `, e);
      });
    }

    this.saveState = {
      document: this.localDB.get(patientNumber || '')
    };
  }
  /**
   * saveToPouch is the public wrapper for the pouch/couch save functionality.  It takes
   * in the documentId and documentObj.
   * @documentId A string corresponding to the name of the couch/pouch document you are
   * referenceing. This is the patient number for the current database object.
   * @documentObj An object that is a copy of the current document corresponding to the
   * requested documentId.  Has to have the _id adn _rev properties inorder to be saved
   * to pouch/couch.
   */


}
export default DatabaseTools;